/**
 * Sprint-11 — Public external-reviewer landing page (magic-link + OTP gate).
 *
 * URL shape:    /external/grant?token=<magic_link_token>
 *
 * Flow:
 *   1. On mount we POST the opaque magic-link token to
 *      `/external/grant/start-otp`. The BE resolves it to a grant, mints a
 *      fresh 6-digit OTP, hashes + persists it, and emails the plaintext
 *      code to the reviewer. The response carries the (un-redacted)
 *      reviewer email — we render it back masked so the reviewer can
 *      confirm the destination without exposing the full address on screen.
 *   2. Reviewer types the 6-digit code into a single autofocused, numeric
 *      input. On submit we POST it to `/external/grant/verify-otp`. On
 *      success the BE returns the scope-bounded session JWT (`kind:
 *      'external_review'`).
 *   3. We persist the session token + grant id + scope binder id under
 *      `dreamit2.external.session` in localStorage (deliberately a
 *      different key than the internal `dreamit2:session`) and navigate
 *      to `/external/binders/{scope_binder_id}`.
 *
 * NO AppShell — this is a standalone unauthenticated route rendered as a
 * centered card on a neutral background. Coordinators may legitimately
 * open the link in a browser that already has a staff session — we MUST
 * NOT touch that session.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertTriangle, MailCheck } from 'lucide-react';
import { externalAccess } from '@/api/client';
import type { ApiError } from '@/api/client';

/** localStorage key for the external-reviewer session payload (see ExternalBinderPage). */
const EXTERNAL_SESSION_KEY = 'dreamit2.external.session';
const OTP_LEN = 6;

interface PersistedExternalSession {
  session_token: string;
  grant_id: string;
  scope_binder_id: string;
}

/** Mask jane.doe@example.com → ja***@example.com (BE-side masking is mirrored here for resilience). */
function maskEmail(value: string): string {
  if (!value) return '';
  const at = value.indexOf('@');
  if (at < 0) return value;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  const head = local.slice(0, 2);
  return `${head}***@${domain}`;
}

type LandingState =
  | { kind: 'loading_link' }
  | { kind: 'link_error'; message: string }
  | { kind: 'awaiting_otp'; grantId: string; emailMasked: string; expiresAt: string };

export default function ExternalGrantLandingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const magicLinkToken = params.get('token') ?? '';

  const [state, setState] = useState<LandingState>({ kind: 'loading_link' });
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const otpInputRef = useRef<HTMLInputElement | null>(null);
  // Guard against React 18 StrictMode double-invocation in dev — startOtp
  // mutates server state (rotates the OTP) so a duplicate call would email
  // the reviewer two codes a second apart and the first would be invalid.
  const sentRef = useRef(false);

  // Kick off the OTP send on mount.
  useEffect(() => {
    if (!magicLinkToken) {
      setState({
        kind: 'link_error',
        message:
          'This link is missing the access token. Open it from the email exactly as it arrived.',
      });
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;

    externalAccess
      .startOtp(magicLinkToken)
      .then((r) => {
        setState({
          kind: 'awaiting_otp',
          grantId: r.grant_id,
          emailMasked: maskEmail(r.email),
          expiresAt: r.otp_expires_at,
        });
      })
      .catch((e: unknown) => {
        const err = e as ApiError;
        if (err?.status === 404) {
          setState({
            kind: 'link_error',
            message:
              'This link is invalid or has already been used. Contact the site coordinator for a new link.',
          });
        } else if (err?.message?.toLowerCase().includes('expired')) {
          setState({
            kind: 'link_error',
            message:
              'This link has expired. Contact the site coordinator to receive a new access link.',
          });
        } else if (err?.message?.toLowerCase().includes('revoked')) {
          setState({
            kind: 'link_error',
            message:
              'This grant has been revoked. Contact the site coordinator if you still need access.',
          });
        } else {
          setState({
            kind: 'link_error',
            message:
              err?.message ?? 'Something went wrong opening this link. Please try again shortly.',
          });
        }
      });
  }, [magicLinkToken]);

  // Autofocus the OTP input as soon as we transition into `awaiting_otp`.
  useEffect(() => {
    if (state.kind === 'awaiting_otp') {
      // Defer to the next tick so React has actually mounted the input.
      const id = window.setTimeout(() => otpInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [state.kind]);

  async function submitOtp() {
    if (state.kind !== 'awaiting_otp') return;
    if (otp.length !== OTP_LEN || !/^\d+$/.test(otp)) {
      setVerifyError('Enter the 6-digit code from the email.');
      return;
    }
    setVerifying(true);
    setVerifyError(null);
    try {
      const r = await externalAccess.verifyOtp(state.grantId, otp);

      // We need the binder id to navigate, but `verifyOtp` only returns the
      // session_token. Decoding a JWT payload client-side is fine for routing
      // (the BE re-verifies on every read), and is the simplest way to honour
      // the spec's "navigate to /external/binders/{scope_binder_id}".
      const scopeBinderId = readScopeBinderId(r.session_token);
      if (!scopeBinderId) {
        setVerifyError(
          'Verified, but the session is missing a binder reference. Contact the site coordinator.',
        );
        return;
      }

      const payload: PersistedExternalSession = {
        session_token: r.session_token,
        grant_id: state.grantId,
        scope_binder_id: scopeBinderId,
      };
      try {
        localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(payload));
      } catch {
        // Some browsers (private windows, storage quota) refuse localStorage.
        // The binder page falls back gracefully — but without persistence the
        // reviewer would be sent straight back to /external/grant. Surface
        // the issue rather than silently looping them.
        setVerifyError(
          'Could not persist your session. Enable storage for this site, then try again.',
        );
        return;
      }
      navigate(`/external/binders/${encodeURIComponent(scopeBinderId)}`, { replace: true });
    } catch (e: unknown) {
      const err = e as ApiError;
      const lower = err?.message?.toLowerCase() ?? '';
      if (lower.includes('too many') || lower.includes('revoked')) {
        setVerifyError(
          'Too many attempts. This grant has been locked — contact the site coordinator.',
        );
        setOtp('');
      } else if (lower.includes('expired')) {
        setVerifyError(
          'This code has expired. Re-open the email link to request a new code.',
        );
        setOtp('');
      } else if (lower.includes('invalid code')) {
        setVerifyError('Incorrect code. Check the email and try again.');
        setOtp('');
      } else if (err?.status === 404) {
        setVerifyError('Grant not found. Re-open the email link to start over.');
      } else {
        setVerifyError(err?.message ?? 'Verification failed. Please try again.');
        setOtp('');
      }
    } finally {
      setVerifying(false);
    }
  }

  if (state.kind === 'loading_link') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-6 text-ink-muted">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Opening your access link…</p>
        </div>
      </Shell>
    );
  }

  if (state.kind === 'link_error') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <AlertTriangle className="h-8 w-8 text-danger-600" />
          <h1 className="text-base font-semibold text-ink-primary">Access link unavailable</h1>
          <p className="text-xs text-ink-muted">{state.message}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col items-center gap-2 text-center">
        <MailCheck className="h-8 w-8 text-accent-600" />
        <h1 className="text-base font-semibold text-ink-primary">Check your email</h1>
        <p className="text-xs text-ink-muted">
          We sent a 6-digit code to{' '}
          <span className="font-medium text-ink-primary">{state.emailMasked}</span>. Enter it below
          to continue.
        </p>
      </div>

      <div className="mt-6 flex flex-col items-center gap-2">
        <label
          htmlFor="external-otp"
          className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted"
        >
          Verification code
        </label>
        <input
          ref={otpInputRef}
          id="external-otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={OTP_LEN}
          value={otp}
          disabled={verifying}
          onChange={(e) => {
            const next = e.target.value.replace(/\D/g, '').slice(0, OTP_LEN);
            setOtp(next);
            if (verifyError) setVerifyError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submitOtp();
            }
          }}
          placeholder="000000"
          className={`h-14 w-56 rounded border text-center font-mono text-2xl tracking-[0.5em] text-ink-primary placeholder:tracking-normal placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-accent-400 ${
            verifyError ? 'border-danger-400' : 'border-surface-border'
          }`}
          aria-invalid={!!verifyError}
          aria-describedby={verifyError ? 'external-otp-error' : undefined}
        />
        {verifyError && (
          <p id="external-otp-error" className="text-center text-xs text-danger-600">
            {verifyError}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => void submitOtp()}
        disabled={verifying || otp.length !== OTP_LEN}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {verifying ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
          </>
        ) : (
          <>
            <ShieldCheck className="h-4 w-4" /> Verify and continue
          </>
        )}
      </button>

      <p className="mt-4 text-center text-[11px] text-ink-muted">
        Didn't get a code? Re-open the email link or contact the site coordinator.
      </p>
    </Shell>
  );
}

/**
 * Decode the scope_binder_id claim out of the freshly-minted external-review
 * JWT. This is purely a client-side routing convenience — the BE re-verifies
 * the signature on every `/external/binders/*` read, so a tampered claim
 * here cannot extend access.
 */
function readScopeBinderId(jwt: string): string | null {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    if (!payload) return null;
    // base64url → base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const parsed = JSON.parse(json) as { scope_binder_id?: unknown };
    return typeof parsed.scope_binder_id === 'string' ? parsed.scope_binder_id : null;
  } catch {
    return null;
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-base">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-4 py-10">
        <div className="mb-6 text-center">
          <div className="text-lg font-semibold text-ink-primary">DreamTrials</div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">
            External Review Access
          </div>
        </div>
        <div className="rounded border border-surface-border bg-surface-card p-6 shadow-sm">
          {children}
        </div>
        <p className="mt-6 text-center text-[11px] text-ink-muted">
          Authorized external access. All views are logged.
        </p>
      </div>
    </div>
  );
}
